import React from "react";
import styled, { css, keyframes } from "styled-components";
import Image from "next/image";
import { TheButton } from "components/TheComponents/TheButton";
import { useAppContext } from "contexts";
import RevealSection from "components/common/RevealSection";
import useRevealGate from "hooks/useRevealGate";

const HERO_BG = "/images/HomeLoginComponent/section-bg.svg";

function HomeLoginComponent() {
  const { loginModalProps, profile } = useAppContext();
  const isGuest = !profile?.displayName;
  const [isLogoLoaded, setIsLogoLoaded] = React.useState(false);
  const { isReady } = useRevealGate({
    preload: isGuest ? [HERO_BG] : [],
  });

  const handleLogoLoad = () => {
    setIsLogoLoaded(true);
  };

  return (
    <Container className="container">
      <RevealSection isReady={isReady}>
        {profile?.displayName ? (
          <Header>
            <h2 className="title">
              {`Welcome, `}
              <span className="highlight">{profile?.displayName}</span>!
            </h2>
            <p className="subtitle">
              We hope you are having a great and lucky day! Enjoy your WRewards!
            </p>
          </Header>
        ) : (
          <Content>
            <div className="animated">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((item, key) => {
                return (
                  <div className={`item item-${item}`} key={`${item}-${key}`}>
                    <Image
                      src={`/images/HomeLoginComponent/${item}.png`}
                      alt=""
                      fill
                      sizes="80px"
                    />
                  </div>
                );
              })}
            </div>
            <MainImage $isLoaded={isLogoLoaded}>
              <Image
                src={"/images/HomeLoginComponent/main-logo-2.png"}
                fill
                alt=""
                sizes="(max-width: 500px) 100vw, 500px"
                style={{ objectFit: "contain", filter: "drop-shadow(#141722 0rem 0rem 2rem)" }}
                priority
                onLoad={handleLogoLoad}
                onError={handleLogoLoad}
              />
            </MainImage>
            <p className="text">
              A VIP experience through personalized rewards, leaderboards, raffles & more! Earn your
              rewards on favorite casinos everyday!
            </p>
            <TheButton
              styles={css`
                white-space: nowrap;
                width: auto;
                min-width: 300px;
              `}
              preset="blue"
              onClick={() => loginModalProps.onOpenLogin()}
            >
              Log in
            </TheButton>
          </Content>
        )}
      </RevealSection>
    </Container>
  );
}

const Header = styled.div`
  margin-top: 4rem;

  .title {
    text-transform: uppercase;
    font-style: italic;
    font-family: var(--font-family-exo);
    font-weight: 900;
    color: var(--daylight-daylight-main, #d5e0f5);
    font-size: 1.5rem;
  }

  .subtitle {
    font-family: var(--font-family-exo);
    font-weight: 400;

    font-size: 1.125rem;
    color: #abbadb;
  }

  .highlight {
    color: #00b2ff;
  }
`;

const Container = styled.div`
  margin: 1rem auto;
`;

const fall = keyframes`
    0% {
        transform: translateY(-120%);
        opacity: 1;
    }
    40% {
        transform: translateY(600px);
        opacity: 0.5;
    }
    100% {
        transform: translateY(600px);
        opacity: 0;
    }
`;

const MainImage = styled.div<{ $isLoaded: boolean }>`
  position: relative;
  min-height: 130px;
  max-width: 500px;
  width: 100%;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
  transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Content = styled.div`
  background: url(${HERO_BG}), var(--Dark-Midnight, #141722);
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  overflow: hidden;
  position: relative;

  .animated {
    position: absolute;
    inset: 0;

    .item {
      position: absolute;
      animation: ${fall} ease-in infinite;
      animation-fill-mode: backwards;
    }

    .item-1 {
      width: 80px;
      height: 80px;
      left: 10%;
      animation-duration: 3.5s;
      animation-delay: 1s;
    }

    .item-2 {
      width: 50px;
      height: 50px;
      left: 15%;
      animation-duration: 3.5s;
      animation-delay: 0.5s;
    }

    .item-3 {
      width: 60px;
      height: 60px;
      left: 20%;
      animation-duration: 3.5s;
      animation-delay: 1.5s;
    }

    .item-4 {
      width: 120px;
      height: 120px;
      left: 5%;
      animation-duration: 3.5s;
      animation-delay: 2s;
    }

    .item-5 {
      width: 80px;
      height: 80px;
      right: 5%;
      animation-duration: 3.5s;
      animation-delay: 1s;
    }

    .item-6 {
      width: 50px;
      height: 50px;
      right: 25%;
      animation-duration: 3.5s;
      animation-delay: 0.5s;
    }

    .item-7 {
      width: 60px;
      height: 60px;
      right: 10%;
      animation-duration: 3.5s;
      animation-delay: 1.5s;
    }

    .item-8 {
      width: 120px;
      height: 120px;
      right: 15%;
      animation-duration: 3.5s;
      animation-delay: 2s;
    }
  }

  .text {
    font-family: var(--font-family-golos);
    margin: 0;
    max-width: 500px;
    text-align: center;
    color: var(--daylight-daylight-75, rgba(200, 215, 245, 0.75));
  }
`;

export default HomeLoginComponent;
